import { useCallback, useMemo, useRef } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Check,
  ExternalLink,
  type LucideIcon,
  RefreshCw,
  SlashSquare,
  X
} from 'lucide-react';
import {
  AnimatePresence,
  motion,
  type Transition,
  useReducedMotion
} from 'motion/react';

import { cn } from '@/shadcn/lib/utils';
import { Skeleton } from '@/shadcn/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { BatchItemErrorDetail } from '@/components/batchActionHistory/batchItemErrorDialog';
import type { BatchItemEvent } from '@/hooks/useRecentBatchItems';

type Props = {
  items: BatchItemEvent[];
  latestArrivalIds: string[];
  onLoadOlder: () => void;
  // When set, FAILED rows show a per-row "Retry" button that re-runs just that
  // trace (STMN-3361). `retryingItems` holds trace-ids currently being retried
  // so their button shows a pending state and can't be double-clicked.
  onRetryItem?: (traceId: string) => void;
  retryingItems?: ReadonlySet<string>;
  // While the first page is in flight, show placeholder rows: "No completed rows
  // yet" is a claim about the job, and it is wrong when we simply haven't looked.
  loading?: boolean;
};

// Pre-measure estimates. The real height is still measured per row via
// virtualizer.measureElement, but these MUST be accurate: a row sits at its
// estimated offset until its ResizeObserver measurement lands, so an estimate
// that is too tall renders a visibly oversized gap that then collapses — and now
// that offsets animate, that collapse is a visible slide rather than a 1-frame
// snap.
//
// A row is the card plus its 6px bottom gutter (pb-1.5). The card's height comes
// from the 24px status chip (`size-6`), not the text — the label line is 16px and
// the trace-id pill 20px, so the chip dominates: 24 + p-2.5 (20) + border (2)
// = 46, + 6 = 52.
const ROW_HEIGHT = 52;
// A row whose error message renders adds a `mt-0.5` (2px) paragraph of truncated
// (so always single-line) 11px text ≈ 16px. The right-hand column then drives the
// height instead of the chip: 20 + 2 + 16 = 38, + 22 chrome = 60, + 6 = 66.
const ROW_HEIGHT_WITH_ERROR = 66;
// Number of leading trace-id characters shown in the pill; the full id is the
// link href + title. Trace ids are uuids, so 8 chars is a recognisable prefix
// without letting the raw id dominate the row (the pre-redesign bug).
const TRACE_ID_SHORT = 8;
// Fetch the next page a few rows before the bottom, so paging feels continuous
// rather than stalling at the edge.
const LOAD_OLDER_THRESHOLD_PX = 240;
// Placeholder rows while the first page loads.
const SKELETON_ROWS = 6;

// --- Motion: "a card is dealt onto a stack" -------------------------------
// The effect has two halves that must stay in sync: settled rows GLIDE down to
// their new offset while the arriving card LANDS in the gap that just opened.
// Tuning one without the other is what makes it read as a snap plus a fade.

// easeOutQuint control points — leaves immediately, decelerates into place.
const EASE_OUT_QUINT: [number, number, number, number] = [0.22, 1, 0.36, 1];
// The glide. A tween, not a spring: every settled row travels a different
// distance (one card height per arrival in the burst), and a fixed window makes
// them all arrive together instead of each finding its own settling time.
const SHIFT_TRANSITION: Transition = {
  type: 'tween',
  duration: 0.26,
  ease: EASE_OUT_QUINT
};
// The landing. Critically damped (damping ≈ 2·√(stiffness·mass)) so the card
// glides in with weight but never overshoots or bounces.
const ENTER_SPRING: Transition = {
  type: 'spring',
  stiffness: 260,
  damping: 30,
  mass: 0.85
};
const INSTANT: Transition = { duration: 0 };
// How far above its resting place the card starts. Deliberately small: the
// scroll container clips anything above the first row, so a long drop would be
// visibly cut off at the top edge. The sense of movement comes from the stack
// gliding down, not from the card's own travel.
const ENTER_TRAVEL_PX = 10;
const ENTER_SCALE = 0.96;
// The card waits for the stack to start opening the gap. Without this it fades in
// on top of rows that have not moved out of the way yet, and because the card
// tints are semi-transparent (bg-*/5) both rows show through each other.
const ENTER_DELAY_S = 0.09;
// Rows landing in the same poll cascade instead of popping in together. Capped so
// a 30-row burst does not trail on for two seconds.
const STAGGER_S = 0.055;
const STAGGER_CAP = 6;
const REDUCED_FADE_S = 0.15;

// Start state for the arriving card: a short drop from above, or a plain fade
// when the user prefers reduced motion. `false` means no entrance at all — for
// rows that were merely materialized by scrolling back through history.
function enterInitial(isNew: boolean, reduce: boolean) {
  if (!isNew) {
    return false;
  }
  if (reduce) {
    return { opacity: 0 };
  }
  return { opacity: 0, y: -ENTER_TRAVEL_PX, scale: ENTER_SCALE };
}

function enterTransition(
  isNew: boolean,
  arrivalPos: number | undefined,
  reduce: boolean
): Transition {
  if (!isNew) {
    return INSTANT;
  }
  const delay =
    ENTER_DELAY_S + Math.min(arrivalPos ?? 0, STAGGER_CAP) * STAGGER_S;
  if (reduce) {
    return { duration: REDUCED_FADE_S, delay };
  }
  return { ...ENTER_SPRING, delay };
}

type StatusMeta = {
  Icon: LucideIcon;
  label: string;
  // card tint (bg + border) and the icon-chip foreground/background.
  card: string;
  chip: string;
};

const STATUS_META: Record<string, StatusMeta> = {
  DONE: {
    Icon: Check,
    label: 'Succeeded',
    card: 'border-green-600/20 bg-green-600/5',
    chip: 'bg-green-600/15 text-green-600'
  },
  FAILED: {
    Icon: X,
    label: 'Failed',
    card: 'border-destructive/20 bg-destructive/5',
    chip: 'bg-destructive/15 text-destructive'
  },
  SKIPPED: {
    Icon: SlashSquare,
    label: 'Skipped',
    card: 'border-border bg-muted/40',
    chip: 'bg-muted text-foreground/50'
  }
};

function statusMeta(status: string): StatusMeta {
  return STATUS_META[status] ?? STATUS_META.SKIPPED;
}

function errorMessage(error: unknown): string | null {
  if (!(error && typeof error === 'object' && 'message' in error)) {
    return null;
  }
  const { message } = error;
  return typeof message === 'string' ? message : null;
}

function shortTraceId(traceId: string): string {
  if (traceId.length <= TRACE_ID_SHORT) {
    return traceId;
  }
  return `${traceId.slice(0, TRACE_ID_SHORT)}…`;
}

// A single completion card (status-tinted, icon chip, shortened trace-id pill,
// optional error line). Pure presentation — the row wrapper owns positioning +
// entrance animation, so this never touches transform/layout.
function CompletionCard({
  item,
  onRetryItem,
  retrying = false
}: {
  item: BatchItemEvent;
  onRetryItem?: (traceId: string) => void;
  retrying?: boolean;
}) {
  const meta = statusMeta(item.status);
  const msg = errorMessage(item.error);
  const { Icon } = meta;
  // Per-row retry: only for FAILED rows, and only when a retry handler is wired
  // (the feed is shown on a terminal job). Re-runs just this one trace.
  const canRetry = item.status === 'FAILED' && !!onRetryItem;

  return (
    <div
      className={cn(
        // items-center so the status chip sits between the two lines rather than
        // hugging the first one. `group` drives the error line's "See more".
        'group flex items-center gap-2.5 rounded-md border p-2.5',
        meta.card
      )}
      data-testid={`batch-item-${item.traceId}`}
    >
      <span
        aria-hidden
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-full',
          meta.chip
        )}
        data-testid={`batch-item-icon-${item.traceId}`}
      >
        <Icon size={14} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="font-medium text-xs">{meta.label}</span>
            {item.attempts > 1 && (
              <span
                className="shrink-0 rounded-full bg-foreground/10 px-1.5 py-px text-[10px] text-foreground/60"
                data-testid={`batch-item-attempts-${item.traceId}`}
              >
                {`retried ${item.attempts - 1}×`}
              </span>
            )}
            {canRetry && (
              <button
                className="flex shrink-0 items-center gap-1 rounded px-1 py-0.5 text-[11px] text-foreground/60 hover:bg-foreground/5 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                data-testid={`batch-item-retry-${item.traceId}`}
                disabled={retrying}
                onClick={() => onRetryItem?.(item.traceId)}
                type="button"
              >
                <RefreshCw
                  className={cn('size-3', retrying && 'animate-spin')}
                />
                {retrying ? 'Retrying…' : 'Retry'}
              </button>
            )}
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* A real anchor with target=_blank, not a NavLink: opening the
                  trace must not lose the batch you are reading. */}
              <a
                className={cn(
                  // leading-none: the text box is otherwise taller than the
                  // glyph, which drops the arrow below the label's centre.
                  'flex shrink-0 items-center gap-1 rounded bg-foreground/5 px-1.5 py-0.5 text-[11px] text-foreground/60 leading-none hover:bg-foreground/10 hover:text-foreground',
                  // Monospace only for the raw-id fallback; a human name reads
                  // better in the normal font.
                  !item.traceName && 'font-mono'
                )}
                data-testid={`batch-item-link-${item.traceId}`}
                href={`/trace/${item.traceId}`}
                rel="noreferrer"
                target="_blank"
              >
                {item.traceName ?? shortTraceId(item.traceId)}
                <ExternalLink aria-hidden className="size-3 shrink-0" />
              </a>
            </TooltipTrigger>
            {/* The pill can only show a short id when the trace has no name, so
                the tooltip carries the identity the reader needs to recognize
                (and read out) the trace. */}
            <TooltipContent side="left">
              {item.traceName
                ? `Open ${item.traceName}`
                : `Open trace ${item.traceId}`}
            </TooltipContent>
          </Tooltip>
        </div>
        {msg && (
          <BatchItemErrorDetail
            error={item.error}
            traceId={item.traceId}
            traceName={item.traceName}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Live completion feed: virtualized, newest-first list of settled batch items.
 * Each row is a status-tinted card. NEW arrivals (latestArrivalIds) animate in;
 * rows materialized by scroll-back do not (avoids an animation storm).
 *
 * Positioning and entrance are on SEPARATE elements: the outer motion.div owns
 * the virtualizer's `start` offset (and is measured for its real height), while
 * an inner motion.div owns the entrance transform. They must stay split —
 * driving both from one element makes the entrance `y` clobber the virtual
 * offset, so every new row snaps to translateY(0) and they pile up on each other.
 *
 * The outer offset is an ANIMATION TARGET (`animate={{ y: start }}`), never an
 * inline `transform`. That is the whole card-stack effect: prepending an arrival
 * shifts every row below it by one card height, and animating that shift is what
 * makes the stack glide down to make room. Written as a static transform the
 * rows teleport in a single frame and only the new card appears to animate.
 */
export function BatchCompletionFeed({
  items,
  latestArrivalIds,
  onLoadOlder,
  onRetryItem,
  retryingItems,
  loading = false
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // useReducedMotion returns null until the media query resolves; treat that as
  // "no preference" so the helpers below take a plain boolean.
  const reduce = useReducedMotion() ?? false;
  // traceId → its order within the latest arrival batch, for a staggered
  // entrance when several rows land in the same poll. Absent = not a new
  // arrival (rendered without animation, e.g. scroll-back rows).
  const arrivalIndex = useMemo(() => {
    const map = new Map<string, number>();
    latestArrivalIds.forEach((id, i) => map.set(id, i));
    return map;
  }, [latestArrivalIds]);

  // Measured sizes are cached by getItemKey, which defaults to the INDEX. The
  // feed is newest-first and arrivals are prepended, so every item changes index
  // on every poll — with the default each item would inherit the measured height
  // of whatever used to occupy its new slot, and since FAILED rows are ~14px
  // taller than the rest, heights get misassigned across the whole list. Keying
  // by traceId makes a measurement follow its item.
  const getItemKey = useCallback(
    (index: number) => items[index]?.traceId ?? index,
    [items]
  );
  // Content-aware so an unmeasured row lands on its real offset immediately.
  // Mirrors the render condition exactly: the paragraph only exists when the
  // error actually carries a message.
  const estimateSize = useCallback(
    (index: number) =>
      errorMessage(items[index]?.error) ? ROW_HEIGHT_WITH_ERROR : ROW_HEIGHT,
    [items]
  );

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize,
    getItemKey,
    overscan: 8
  });

  if (loading && !items.length) {
    return (
      <div
        className="flex flex-col gap-1.5 p-0.5"
        data-testid="batch-feed-loading"
      >
        {Array.from({ length: SKELETON_ROWS }, (_, i) => (
          <Skeleton className="h-[46px] w-full rounded-md" key={i} />
        ))}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div
        className="flex h-24 items-center justify-center text-foreground/50 text-xs"
        data-testid="batch-feed-empty"
      >
        No completed rows yet.
      </div>
    );
  }

  return (
    // The feed fills its flex parent (h-full) rather than a fixed cap, using the
    // drawer's available height.
    <TooltipProvider delayDuration={200}>
      <div className="h-full min-h-0">
        <div
          className="h-full overflow-auto"
          data-testid="batch-completion-feed"
          onScroll={e => {
            // The feed renders NEWEST-FIRST, so older history is at the BOTTOM.
            // Paging off the top edge meant scrolling down loaded nothing and you
            // had to scroll back up to fetch the next page.
            const el = e.currentTarget;
            const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (remaining < LOAD_OLDER_THRESHOLD_PX) {
              onLoadOlder();
            }
          }}
          ref={scrollRef}
        >
          <div
            style={{ height: virtualizer.getTotalSize(), position: 'relative' }}
          >
            <AnimatePresence initial={false}>
              {virtualizer.getVirtualItems().map(v => {
                const item = items[v.index];
                const arrivalPos = arrivalIndex.get(item.traceId);
                const isNew = arrivalPos !== undefined;
                return (
                  // OUTER: owns the row's offset and is the measured element.
                  // motion owns `y` EXCLUSIVELY — do not also put a `transform` in
                  // `style`, motion's y wins and every row collapses to 0.
                  // `initial={false}` pins a row at its offset on mount, so rows
                  // materialized by scrolling do not fly in from the top; only
                  // later CHANGES to v.start animate, and that is the push-down.
                  <motion.div
                    animate={{ y: v.start }}
                    data-index={v.index}
                    data-testid={`batch-row-${item.traceId}`}
                    initial={false}
                    key={item.traceId}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      // Arrivals paint above settled rows, so the stack slides down
                      // UNDERNEATH the new card instead of across the top of it.
                      zIndex: isNew ? 1 : 0
                    }}
                    transition={reduce ? INSTANT : SHIFT_TRANSITION}
                  >
                    <div className="px-0.5 pb-1.5">
                      {/* INNER: entrance only. Its own element, so this transform
                        never fights the outer offset. Always a motion.div (not
                        conditional) — swapping element types when a row stops
                        being "new" would remount the card on the next poll. */}
                      <motion.div
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        initial={enterInitial(isNew, reduce)}
                        transition={enterTransition(isNew, arrivalPos, reduce)}
                      >
                        <CompletionCard
                          item={item}
                          onRetryItem={onRetryItem}
                          retrying={retryingItems?.has(item.traceId)}
                        />
                      </motion.div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
